import { useEffect, useState } from "react";
import { BellOff, BellRing, Users, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const MODES = [
  { key: "default", label: "Default", hint: "Head coach of the team (booker if no team)" },
  { key: "custom", label: "Send to…", hint: "Specific people instead of the head coach" },
  { key: "mute", label: "Mute", hint: "No pre- or post-game nudges for this game" },
];

export function RefNudgeTag({ ov }) {
  if (!ov) return null;
  return ov.mode === "mute"
    ? <span className="text-[9px] tracking-widest uppercase px-1.5 py-0.5 rounded border border-border opacity-70 inline-flex items-center gap-1" data-testid="ref-nudge-tag" title={ov.reason || ""}><BellOff className="w-3 h-3" /> Nudges muted</span>
    : <span className="text-[9px] tracking-widest uppercase px-1.5 py-0.5 rounded border border-[#83B68B]/50 text-[#83B68B] bg-[#83B68B]/10 inline-flex items-center gap-1" data-testid="ref-nudge-tag"><BellRing className="w-3 h-3" /> Nudges → {(ov.user_names || []).join(", ")}</span>;
}

export function PostMatchOutstanding({ missing }) {
  if (!missing?.length) return null;
  return (
    <div className="p-3 rounded border border-amber-400/40 bg-amber-500/5 text-xs flex items-start gap-2" data-testid="post-match-outstanding">
      <ClipboardList className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
      <div>
        <div className="text-amber-300 font-semibold">Post-match admin outstanding</div>
        <div className="opacity-80 mt-0.5">{missing.join(" · ")}. The head coach is reminded daily until this is done.</div>
      </div>
    </div>
  );
}

export function RefNudgeControl({ booking, onSaved }) {
  const ov = booking.ref_nudge || null;
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState(ov?.mode || "default");
  const [ids, setIds] = useState(ov?.user_ids || []);
  const [reason, setReason] = useState(ov?.reason || "");
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setMode(ov?.mode || "default"); setIds(ov?.user_ids || []); setReason(ov?.reason || ""); }, [booking.id]);
  useEffect(() => { if (open && mode === "custom" && !users.length) api.get("/admin/users").then(r => setUsers(r.data.filter(u => u.is_active !== false))).catch(() => {}); }, [open, mode]);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/bookings/${booking.id}/ref-nudge`, { mode, user_ids: mode === "custom" ? ids : [], reason: mode === "mute" ? reason : null });
      toast.success(mode === "default" ? "Nudges back to the head coach" : mode === "mute" ? "Nudges muted for this game" : "Nudges will go to the people you picked");
      setOpen(false); onSaved?.(data.ref_nudge);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const shown = users.filter(u => !q || (u.name || "").toLowerCase().includes(q.toLowerCase()) || (u.email || "").toLowerCase().includes(q.toLowerCase())).slice(0, 12);

  return (
    <div className="rounded border border-border p-3 text-xs" data-testid="ref-nudge-control">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="inline-flex items-center gap-2"><BellRing className="w-3.5 h-3.5 text-[#83B68B]" /><span className="tracking-widest uppercase text-[10px] opacity-70">Referee nudges</span><RefNudgeTag ov={ov} />{!ov && <span className="opacity-60">Default · head coach</span>}</div>
        <button type="button" onClick={() => setOpen(o => !o)} className="text-[10px] tracking-widest uppercase px-2 py-1 rounded border border-border hover:border-[#83B68B] hover:text-[#83B68B]" data-testid="ref-nudge-toggle">{open ? "Close" : "Change"}</button>
      </div>
      {open && (
        <div className="mt-3 space-y-3">
          <div className="grid grid-cols-3 gap-2">
            {MODES.map(m => (
              <button key={m.key} type="button" onClick={() => setMode(m.key)} data-testid={`ref-nudge-mode-${m.key}`}
                className={`text-left rounded border p-2 ${mode === m.key ? "border-[#83B68B] bg-[#83B68B]/10" : "border-border hover:border-[#83B68B]/50"}`}>
                <div className={`font-semibold ${mode === m.key ? "text-[#83B68B]" : ""}`}>{m.label}</div>
                <div className="opacity-60 text-[11px] leading-snug mt-0.5">{m.hint}</div>
              </button>
            ))}
          </div>
          {mode === "custom" && (
            <div data-testid="ref-nudge-people">
              <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search people…" className="w-full bg-transparent border border-border rounded px-2 py-1.5 outline-none focus:border-[#83B68B]" data-testid="ref-nudge-search" />
              <div className="mt-2 max-h-40 overflow-y-auto space-y-1">
                {shown.map(u => (
                  <label key={u.id} className="flex items-center gap-2 cursor-pointer hover:text-[#83B68B]" data-testid={`ref-nudge-user-${u.id}`}>
                    <input type="checkbox" checked={ids.includes(u.id)} onChange={e => setIds(s => e.target.checked ? [...s, u.id] : s.filter(x => x !== u.id))} className="accent-[#83B68B]" />
                    <span>{u.name || u.email}</span><span className="opacity-50">{u.email}</span>
                  </label>
                ))}
              </div>
              {ids.length > 0 && <div className="mt-1 opacity-70 inline-flex items-center gap-1"><Users className="w-3 h-3" /> {ids.length} selected</div>}
            </div>
          )}
          {mode === "mute" && <input value={reason} onChange={e => setReason(e.target.value)} placeholder="Reason (optional)" className="w-full bg-transparent border border-border rounded px-2 py-1.5 outline-none focus:border-[#83B68B]" data-testid="ref-nudge-reason" />}
          <button type="button" onClick={save} disabled={busy || (mode === "custom" && !ids.length)} className="btn-volt text-[10px] tracking-widest uppercase px-3 py-1.5 rounded disabled:opacity-40" data-testid="ref-nudge-save">{busy ? "Saving…" : "Save"}</button>
        </div>
      )}
    </div>
  );
}
